import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/GlobalProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { Fetch_Orders_History } from "../../hooks/APIHooks";

function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("All");
  const [imageErrors, setImageErrors] = useState({});

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return '';
    }
    
    if (imagePath.startsWith('http')) return imagePath;
    
    const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
    
    // Handle different environments
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      // React dev server on port 3000, APIs on port 80
      baseUrl = `${protocol}//${hostname}`;
    } else if (hostname === 'localhost' && !port) {
      // React build served from same domain as APIs
      baseUrl = window.location.origin;
    } else {
      // Production environment
      baseUrl = window.location.origin;
    }
    
    return `${baseUrl}/apis/${cleanPath}`;
  };

  const handleImageError = (orderId, itemIndex) => {
    setImageErrors(prev => ({
      ...prev,
      [`${orderId}-${itemIndex}`]: true
    }));
  };

  const handleImageLoad = (orderId, itemIndex) => {
    setImageErrors(prev => ({
      ...prev,
      [`${orderId}-${itemIndex}`]: false
    }));
  };

  // Extract query parameters from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get("filter");
    setFilter(filterParam || "All");
  }, [location.search]);

  // Fetch user orders from the API
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(Fetch_Orders_History, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: user.user_id }),
        });
        const data = await response.json();
        if (data.success) {
          setOrders(data.orders);
          setFilteredOrders(data.orders);
        } else {
          setError(data.message || "Failed to fetch orders");
        }
      } catch {
        setError("An error occurred while fetching orders.");
      }
    };

    if (user?.user_id) fetchOrders();
  }, [user.user_id]);

  // Handle filter change and update filtered orders
  const handleFilterChange = (status) => {
    setFilter(status);
    navigate(`?filter=${status}`);
    setFilteredOrders(
      status === "All"
        ? orders
        : orders.filter((order) => order.delivery_status === status)
    );
  };

  // Navigate to order details page
  const handleOrderClick = (order) => {
    navigate("/user/orders/orderdetails", { state: { order } });
  };

  const getFallbackContent = (productName) => {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-lg">
        <div className="text-center">
          <div className="text-lg font-bold text-gray-600">
            {productName ? productName.charAt(0).toUpperCase() : '?'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            No Image
          </div>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <section className="min-h-screen bg-gray-50">
      {/* Filter Section */}
      <section className="px-5 pt-5">
        <h3 className="font-bold text-xl text-center py-5 text-primary">Your Orders</h3>
        <div
          className="flex space-x-2 text-[14px] overflow-x-auto scrollbar-hide pb-2"
          style={{ scrollbarWidth: "none" }}
        >
          {["All", "Processing", "Delivered", "Return", "Cancelled"].map(
            (status) => (
              <button
                key={status}
                onClick={() => handleFilterChange(status)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  filter === status 
                    ? "bg-primary text-white shadow-md" 
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
              >
                {status}
              </button>
            )
          )}
        </div>
      </section>

      {/* Orders Section */}
      <section className="mt-4 px-5 pb-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-700 text-center">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm">
          {filteredOrders.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <li
                  key={order.order_id}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleOrderClick(order)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Order #{order.order_id}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-gray-900">
                        ₹{order.total_amount}
                      </p>
                      <p className={`text-sm font-medium ${
                        order.payment_status === "Completed" 
                          ? "text-green-600" 
                          : order.payment_status === "Pending"
                          ? "text-yellow-600"
                          : "text-red-600"
                      }`}>
                        {order.payment_status}
                      </p>
                    </div>
                  </div>

                  <ul className="space-y-3">
                    {JSON.parse(order.cart_items).map((item, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between pb-3 border-b border-gray-100 last:border-b-0 last:pb-0"
                      >
                        {/* Left Section: Product Details */}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[14px] text-gray-900 truncate">
                            {item.variant || item.product_name}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Qty: {item.quantity}
                          </div>
                          <div className="flex items-center space-x-4 mt-2">
                            <div className="text-[12px]">
                              <span className="text-gray-500">Payment:</span>
                              <br />
                              <span className={`font-semibold ${
                                order.payment_status === "Completed" 
                                  ? "text-green-600" 
                                  : order.payment_status === "Pending"
                                  ? "text-yellow-600"
                                  : "text-red-600"
                              }`}>
                                {order.payment_status}
                              </span>
                            </div>
                            <div className="text-[12px]">
                              <span className="text-gray-500">Shipping:</span>
                              <br />
                              <span className={`font-semibold ${
                                order.delivery_status === "Delivered" 
                                  ? "text-green-600" 
                                  : order.delivery_status === "Processing"
                                  ? "text-blue-600"
                                  : "text-gray-600"
                              }`}>
                                {order.delivery_status}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right Section: Product Image */}
                        <div className="w-20 h-20 ml-4 flex-shrink-0">
                          {!imageErrors[`${order.order_id}-${index}`] ? (
                            <img
                              src={getImageUrl(item.product_image)}
                              alt={item.product_name}
                              className="w-full h-full object-cover rounded-lg shadow-sm"
                              onError={() => handleImageError(order.order_id, index)}
                              onLoad={() => handleImageLoad(order.order_id, index)}
                            />
                          ) : (
                            getFallbackContent(item.product_name)
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📦</div>
              <p className="text-gray-500 text-lg mb-2">No orders found</p>
              <p className="text-gray-400 text-sm">
                {filter === "All" 
                  ? "You haven't placed any orders yet." 
                  : `No ${filter.toLowerCase()} orders found.`
                }
              </p>
            </div>
          )}
        </div>
      </section>
    </section>
  );
}

export default Orders;